import {
    updateApplicationContainerItem,
    updateApplicationContainersGrouped,
    createApplicationContainerItem,
    deleteApplicationContainerItem,
    updateApplicationContainer,
} from "services/requests/v1/container";

import {
    AppsContainerGroup,
    AppsContainerModel,
    AppsContainerItem,
    AppsEditMode,
    EditModalDataType,
    HandleItemHasBeenReorderedParams,
    SaveButtonStatusType,
} from "types/apps-container";
import {
    SetRequestError,
    SetStateType,
    SubscribeContentType,
    UnsubscribeContentType,
} from "types/generic";
import { DxRemoveEvent, DxSaveEvent } from "types/devExtreme";

import { makeDeepCopy } from "utils/functions";

type MakeAppsContainersTabFunctionsParams = {
    containers: AppsContainerModel;
    groupData: EditModalDataType[];
    dataRef: React.MutableRefObject<EditModalDataType[]>;
    editModalGroupName: string;
    groupNameInput: string;
    editMode: AppsEditMode;
    containerId: number | null;
    itemsToDeleteIdList: number[];
    setItemsToDeleteIdList: SetStateType<number[]>;
    setContainerId: SetStateType<number | null>;
    setGroupData: SetStateType<EditModalDataType[]>;
    setContainers: SetStateType<AppsContainerModel>;
    setSaveButtonStatus: SetStateType<SaveButtonStatusType>;
    subscribeContent: SubscribeContentType;
    unsubscribeContent: UnsubscribeContentType;
    setError: SetRequestError;
    closeModalfn: () => void;
    updateData: () => any;
    setGroupNameInput: SetStateType<string>;
    setEditMode: SetStateType<AppsEditMode>;
    translate: (id: string, defaultMessage?: string | undefined) => string;
};

export const makeEditAppsModalFunctions = ({
    containers,
    groupData,
    dataRef,
    editModalGroupName,
    groupNameInput,
    editMode,
    containerId,
    itemsToDeleteIdList,
    setItemsToDeleteIdList,
    setContainerId,
    setGroupData,
    setContainers,
    subscribeContent,
    unsubscribeContent,
    setSaveButtonStatus,
    setError,
    closeModalfn,
    updateData,
    setGroupNameInput,
    setEditMode,
    translate,
}: MakeAppsContainersTabFunctionsParams) => {
    const availableOptions = [
        {
            value: true,
            label: translate("app.general.available"),
        },
        {
            value: false,
            label: translate("app.general.not.available"),
        },
    ];

    const visibilityOptions = [
        {
            value: true,
            label: translate("app.general.yes"),
        },
        {
            value: false,
            label: translate("app.general.not"),
        },
    ];

    const tableLabels = {
        addRow: translate("app.devextreme.addrow.label"),
        cancelRowChanges: translate("app.devextreme.cancel.label"),
        deleteRow: translate("app.devextreme.delete.label"),
        editRow: translate("app.devextreme.edit.label"),
        saveRowChanges: translate("app.devextreme.save.label"),
    };

    const requiredField = translate("app.devextreme.required.label");

    const verifyGroupDataPropsChanges = (propName: keyof EditModalDataType) => {
        if (groupData.length !== dataRef.current.length) return true;

        return groupData.some(
            (group, index) =>
                group[propName] !== dataRef.current[index][propName]
        );
    };

    const defineSaveButtonStatus = (): SaveButtonStatusType => {
        if (editModalGroupName !== groupNameInput) {
            return "enabled";
        }

        const propsToCheckIfChanges: Array<keyof EditModalDataType> = [
            "id",
            "visible",
            "title",
            "goAigua",
            "url",
            "icon",
            "installed" as any,
        ];

        for (const prop of propsToCheckIfChanges) {
            if (verifyGroupDataPropsChanges(prop)) return "enabled";
        }

        return "disabled";
    };

    const handleItemHasBeenReordered = ({
        itemData,
        fromIndex,
        toIndex,
    }: HandleItemHasBeenReorderedParams) => {
        let tempGroupData = [...groupData];

        tempGroupData.splice(fromIndex, 1);
        tempGroupData.splice(toIndex, 0, itemData);

        tempGroupData = tempGroupData.map((group, index) => ({
            ...group,
            itemOrder: index,
            containerOrder: index,
        }));

        setGroupData(tempGroupData);
    };

    const handleRemoveRow = (e: DxRemoveEvent<EditModalDataType>) => {
        setItemsToDeleteIdList((prev) => [...prev, e.data.id]);
        setSaveButtonStatus("enabled");
    };

    const changeGroupVisibility = (group: EditModalDataType) => {
        const groupIndexOnList = groupData.findIndex(
            ({ id }) => id === group.id
        );

        const tempGroupData = [...groupData];
        tempGroupData[groupIndexOnList] = { ...group, visible: !group.visible };

        setGroupData(tempGroupData);
    };

    const openEditSubgroupModal = (groupData: AppsContainerGroup) => {
        dataRef.current = makeDeepCopy(groupData.items);
        editModalGroupName = groupData.title;

        setContainerId(groupData.id);
        setGroupData(makeDeepCopy(groupData.items));
        setGroupNameInput(editModalGroupName);
        setEditMode("subgroup");
    };

    const saveSubGroupTableChangesTemporary = ({
        changes,
    }: DxSaveEvent<EditModalDataType>) => {
        const baseItem = {
            allowed: true,
            containerId: containerId,
            goAigua: false,
            installed: true,
            tags: "",
            itemOrder: groupData.length,
            newTab: true,
            roles: [],
            hasLayers: false,
            client: null,
            clientId: null,
        };

        const insert = () => {
            const newItem = { ...baseItem, ...changes[0].data };
            groupData = groupData.filter(
                ({ title }) => title !== newItem.title
            );

            return [...groupData, newItem as AppsContainerItem];
        };

        const remove = () => groupData.slice();

        const update = () => {
            const itemData = changes[0].data as AppsContainerItem;
            const itemIndex = groupData.findIndex(
                ({ id }) => id === itemData.id
            );

            groupData[itemIndex] = { ...groupData[itemIndex], ...itemData };
            return groupData.slice();
        };

        const saveStrategyTable = {
            insert,
            remove,
            update,
        };

        const prepareDataFunction = saveStrategyTable[changes[0].type];

        setGroupData(prepareDataFunction());
    };

    const saveGroupChangesOnRepo = async () => {
        const payload = { ...containers };
        delete payload[editModalGroupName];

        payload[groupNameInput] = groupData as AppsContainerGroup[];
        const contentName = "update-group";

        try {
            subscribeContent(contentName);

            const { data: updatedData } =
                await updateApplicationContainersGrouped(payload);

            setContainers(updatedData);

            unsubscribeContent(contentName, true);
            closeModalfn();
        } catch (error) {
            setError(contentName);
        }
    };

    const saveSubGroupChangesOnRepo = async () => {
        const contentName = "update-subgroup";
        subscribeContent(contentName);

        try {
            if (groupNameInput !== editModalGroupName) {
                let itemData = {} as AppsContainerGroup;

                Object.values(containers).forEach((container) => {
                    container.forEach((group) => {
                        if (group.id === containerId) {
                            itemData = { ...group, title: groupNameInput };
                        }
                    });
                });

                if (itemData.id) {
                    await updateApplicationContainer(itemData);
                }
            }

            if (itemsToDeleteIdList.length) {
                await Promise.all(
                    itemsToDeleteIdList.map(async (id) => {
                        await deleteApplicationContainerItem(id);
                    })
                );
                setItemsToDeleteIdList([]);
            }

            if (groupData.length) {
                await Promise.all(
                    groupData.map(async (item) => {
                        item = item as AppsContainerItem;
                        if (containerId) item.containerId = containerId;

                        return item.id
                            ? await updateApplicationContainerItem(item)
                            : await createApplicationContainerItem(item);
                    })
                );
            }

            await updateData();

            unsubscribeContent(contentName, true);
            closeModalfn();
        } catch (error) {
            setError(contentName);
        }
    };

    const defineSaveChangesOnRepoStrategy = () => {
        return editMode === "group"
            ? saveGroupChangesOnRepo
            : saveSubGroupChangesOnRepo;
    };

    const defineTextBasedOnEditMode = (baseIntlId: string) => {
        return translate(
            `${baseIntlId}_${editMode !== "group" ? "subgroup" : "group"}`
        );
    };

    const hasGoaiguaContent = () => groupData.some((group) => group.goAigua);

    const handleInputChange = (newValue: string) => {
        const numberRegexp = /\d/;
        if (numberRegexp.test(newValue)) return;

        setGroupNameInput(newValue);
    };

    const dxCustomValidation = ({ value }: { value?: boolean }) =>
        typeof value !== "undefined" && value !== null;

    return {
        availableOptions,
        visibilityOptions,
        tableLabels,
        requiredField,
        handleItemHasBeenReordered,
        changeGroupVisibility,
        openEditSubgroupModal,
        saveSubGroupTableChangesTemporary,
        defineSaveChangesOnRepoStrategy,
        defineSaveButtonStatus,
        defineTextBasedOnEditMode,
        hasGoaiguaContent,
        handleInputChange,
        handleRemoveRow,
        dxCustomValidation,
    };
};
